import React from 'react';
import { Box, Card, Slider, Typography } from '@mui/material';
import { Paintbrush } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAppearance } from '../../contexts/AppearanceContext';
import {
  BACKGROUND_THEME_INTENSITY,
  BACKGROUND_THEMES,
  intensityToOpacity,
} from '../backgrounds/backgroundThemes';

interface AppearanceSectionProps {
  variant?: 'page' | 'modal';
}

export const AppearanceSection: React.FC<AppearanceSectionProps> = ({ variant = 'page' }) => {
  const { t } = useTranslation();
  const {
    canCustomizeBackgroundTheme,
    backgroundTheme,
    backgroundThemeIntensity,
    setBackgroundTheme,
    setBackgroundThemeIntensity,
  } = useAppearance();
  const isModal = variant === 'modal';
  const cardClassName = `${
    isModal ? 'w-full' : 'max-w-2xl'
  } mb-4 border border-gray-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800`;

  if (!canCustomizeBackgroundTheme) {
    return null;
  }

  return (
    <Card className={cardClassName} elevation={0}>
      <Box className="mb-5 flex min-w-0 items-start gap-3">
        <Box className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-pink-100 dark:bg-pink-900/30">
          <Paintbrush className="text-pink-600 dark:text-pink-400" size={20} />
        </Box>
        <Box className="min-w-0">
          <Typography variant="subtitle1" className="font-semibold text-gray-900 dark:text-white">
            {t('settings.appearance.title')}
          </Typography>
          <Typography variant="body2" className="mt-1 text-gray-500 dark:text-slate-400">
            {t('settings.appearance.description')}
          </Typography>
        </Box>
      </Box>

      <Typography variant="subtitle2" className="mb-3 font-semibold text-gray-900 dark:text-white">
        {t('settings.appearance.backgroundThemeLabel')}
      </Typography>
      <Box className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {BACKGROUND_THEMES.map((theme) => {
          const isSelected = backgroundTheme === theme.id;
          const previewOpacity =
            theme.id === 'none' ? 1 : intensityToOpacity(backgroundThemeIntensity);

          return (
            <button
              key={theme.id}
              type="button"
              aria-pressed={isSelected}
              onClick={() => void setBackgroundTheme(theme.id)}
              className={`rounded-lg border p-2 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                isSelected
                  ? 'border-blue-500 ring-2 ring-blue-500/40'
                  : 'border-gray-200 hover:border-gray-300 dark:border-slate-700 dark:hover:border-slate-600'
              }`}
            >
              <span
                className={`block h-20 rounded-md border border-black/5 dark:border-white/10 ${theme.previewClassName}`}
                style={{ opacity: previewOpacity }}
              />
              <span className="mt-2 block text-sm font-medium text-gray-900 dark:text-slate-100">
                {t(theme.labelKey)}
              </span>
            </button>
          );
        })}
      </Box>

      <Box className="mt-5">
        <Box className="mb-1 flex items-center justify-between gap-4">
          <Typography variant="subtitle2" className="font-semibold text-gray-900 dark:text-white">
            {t('settings.appearance.intensityLabel')}
          </Typography>
          <Typography variant="body2" className="font-medium text-gray-600 dark:text-slate-300">
            {backgroundThemeIntensity}%
          </Typography>
        </Box>
        <Slider
          value={backgroundThemeIntensity}
          min={BACKGROUND_THEME_INTENSITY.min}
          max={BACKGROUND_THEME_INTENSITY.max}
          step={BACKGROUND_THEME_INTENSITY.step}
          disabled={backgroundTheme === 'none'}
          onChange={(_, value) =>
            setBackgroundThemeIntensity(Array.isArray(value) ? value[0] : value)
          }
          aria-label={t('settings.appearance.intensityLabel')}
        />
        <Typography variant="body2" className="text-gray-500 dark:text-slate-400">
          {t('settings.appearance.intensityHint')}
        </Typography>
      </Box>
    </Card>
  );
};
